import { supabase } from './supabase';
import { getCached, setCache, isRateLimited, setRateLimitCooldown, resetConsecutiveRateLimits, CACHE_TTL, isNylasDisabled, checkAndIncrementBudget } from './nylasCache';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const FETCH_TIMEOUT_MS = 30000;

export interface NylasCalendarEvent {
  id: string;
  calendarId: string;
  title: string;
  description: string | null;
  location: string | null;
  when: {
    startTime?: number;
    endTime?: number;
    startDate?: string;
    endDate?: string;
    object: 'timespan' | 'datespan' | 'date';
  };
  participants: Array<{
    email: string;
    name?: string;
    status: string;
  }>;
  organizer: {
    email: string;
    name?: string;
  };
  status: string;
  busy: boolean;
  readOnly: boolean;
  conferencing?: {
    provider: string;
    details: {
      url?: string;
    };
  };
  recurrence?: string[];
}

export interface CalendarEvent {
  id: string;
  userId: string;
  outlookEventId: string;
  connectionId: string;
  organizerEmail: string;
  organizerName: string | null;
  subject: string;
  bodyPreview: string | null;
  bodyContent: string | null;
  bodyContentType: 'html' | 'text';
  startTime: string;
  endTime: string;
  timezone: string | null;
  location: string | null;
  attendees: any[];
  isAllDay: boolean;
  isCancelled: boolean;
  responseStatus: 'accepted' | 'declined' | 'tentative' | 'none';
  isPsvRelated: boolean;
  dealId: string | null;
  outlookModifiedAt: string | null;
  importance: 'low' | 'normal' | 'high';
  sensitivity: 'normal' | 'personal' | 'private' | 'confidential';
  isOrganizer: boolean;
  showAs: 'free' | 'tentative' | 'busy' | 'oof' | 'workingElsewhere' | 'unknown';
  onlineMeetingUrl: string | null;
  isRecurring: boolean;
  seriesMasterId: string | null;
  rawMetadata: any;
  createdAt: string;
  updatedAt: string;
  syncedAt: string;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return {
    'Authorization': `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
}

async function callNylasCalendar(action: string, params?: Record<string, string>): Promise<any> {
  if (isNylasDisabled()) {
    throw new Error('nylas_disabled: Calendar sync is paused for scheduled maintenance. All other features remain available.');
  }

  if (!checkAndIncrementBudget()) {
    throw new Error('budget_exceeded: Hourly request limit reached. Please try again later.');
  }

  if (isRateLimited()) {
    throw new Error('provider_busy: Microsoft is temporarily limiting access. Please wait a moment and try again.');
  }

  const headers = await getAuthHeaders();
  const url = new URL(`${SUPABASE_URL}/functions/v1/nylas-email`);
  url.searchParams.set('action', action);
  if (params) {
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(url.toString(), { headers, signal: controller.signal });
  } catch (err: any) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      setRateLimitCooldown(45000);
      throw new Error('provider_timeout: Request timed out. The calendar provider may be temporarily unreachable.');
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    const errorMsg = errorData.error || `Request failed: ${response.status}`;
    if (errorMsg.includes('grant_invalid') || errorMsg.includes('re-authenticate') || errorMsg.includes('expired')) {
      throw new Error('grant_invalid: Your Outlook connection has expired. Please reconnect your account.');
    }
    if (errorMsg.includes('rate_limit') || response.status === 429) {
      const retryAfterHeader = response.headers.get('Retry-After');
      const retryAfterBody = errorData.retry_after;
      const retryAfterSeconds = parseInt(retryAfterHeader || retryAfterBody || '0', 10);
      const cooldownMs = retryAfterSeconds > 0 ? retryAfterSeconds * 1000 : undefined;
      setRateLimitCooldown(cooldownMs);
      throw new Error('provider_busy: Microsoft is temporarily busy. Please wait a moment and try again.');
    }
    if (errorMsg.includes('provider_timeout') || response.status === 504) {
      setRateLimitCooldown(30000);
      throw new Error('provider_timeout: The email provider is temporarily unresponsive. Please try again shortly.');
    }
    throw new Error(errorMsg);
  }
  resetConsecutiveRateLimits();
  return response.json();
}

function mapNylasEventToCalendarEvent(event: any, userId: string): CalendarEvent {
  const when = event.when || {};
  let startTime: string;
  let endTime: string;
  let isAllDay = false;

  if (when.start_time && when.end_time) {
    startTime = new Date(when.start_time * 1000).toISOString();
    endTime = new Date(when.end_time * 1000).toISOString();
  } else if (when.start_date && when.end_date) {
    startTime = new Date(when.start_date).toISOString();
    endTime = new Date(when.end_date).toISOString();
    isAllDay = true;
  } else if (when.date) {
    startTime = new Date(when.date).toISOString();
    endTime = new Date(when.date).toISOString();
    isAllDay = true;
  } else {
    startTime = new Date().toISOString();
    endTime = new Date().toISOString();
  }

  const participants = event.participants || [];
  const organizer = event.organizer || {};
  const subject = event.title || '(No Subject)';

  const isPsvRelated = /\bpsv\b|pre-?study|pre-?site|site visit|qualification visit/i.test(subject);

  const myParticipant = participants.find((p: any) => p.status);
  let responseStatus: 'accepted' | 'declined' | 'tentative' | 'none' = 'none';
  if (myParticipant) {
    const s = myParticipant.status?.toLowerCase();
    if (s === 'yes' || s === 'accepted') responseStatus = 'accepted';
    else if (s === 'no' || s === 'declined') responseStatus = 'declined';
    else if (s === 'maybe' || s === 'tentative') responseStatus = 'tentative';
  }

  return {
    id: event.id,
    userId,
    outlookEventId: event.id,
    connectionId: '',
    organizerEmail: organizer.email || '',
    organizerName: organizer.name || null,
    subject,
    bodyPreview: event.description?.substring(0, 200) || null,
    bodyContent: event.description || null,
    bodyContentType: 'text',
    startTime,
    endTime,
    timezone: null,
    location: event.location || null,
    attendees: participants.map((p: any) => ({
      email: p.email,
      name: p.name || p.email,
      status: p.status,
    })),
    isAllDay,
    isCancelled: event.status === 'cancelled',
    responseStatus,
    isPsvRelated,
    dealId: null,
    outlookModifiedAt: null,
    importance: 'normal',
    sensitivity: 'normal',
    isOrganizer: event.organizer?.email === event.participants?.find((p: any) => p.status === 'yes')?.email,
    showAs: event.busy ? 'busy' : 'free',
    onlineMeetingUrl: event.conferencing?.details?.url || null,
    isRecurring: !!(event.recurrence && event.recurrence.length > 0),
    seriesMasterId: event.master_event_id || null,
    rawMetadata: event,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    syncedAt: new Date().toISOString(),
  };
}

export async function listCalendarEvents(limit = 20, startTime?: number, endTime?: number): Promise<CalendarEvent[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const effectiveStart = startTime || Math.floor(Date.now() / 1000);
  const effectiveEnd = endTime || (effectiveStart + 30 * 24 * 60 * 60);

  const cacheKey = `calendar-${limit}-${effectiveStart}-${effectiveEnd}`;
  const cached = getCached<CalendarEvent[]>(cacheKey);
  if (cached) return cached;

  const params: Record<string, string> = {
    limit: String(limit),
    start: String(effectiveStart),
    end: String(effectiveEnd),
  };

  const result = await callNylasCalendar('calendar-list', params);
  const events = result.data || [];
  const mapped = events
    .map((e: any) => mapNylasEventToCalendarEvent(e, user.id))
    .filter((e: CalendarEvent) => !e.isCancelled);
  setCache(cacheKey, mapped, CACHE_TTL.CALENDAR_EVENTS);
  return mapped;
}

export async function getUpcomingEventsFromNylas(limit = 10): Promise<CalendarEvent[]> {
  return listCalendarEvents(limit);
}

export async function syncCalendarFromNylas(): Promise<{ syncedCount: number }> {
  const events = await listCalendarEvents(20);
  return { syncedCount: events.length };
}

export async function getPSVEvents(): Promise<CalendarEvent[]> {
  const events = await listCalendarEvents(50);
  return events.filter(e => e.isPsvRelated);
}

export async function linkEventToDeal(eventId: string, dealId: string | null): Promise<void> {
  const { error } = await supabase
    .from('calendar_events')
    .update({ deal_id: dealId })
    .eq('id', eventId);

  if (error) {
    console.error('Error linking event to deal:', error);
    throw error;
  }
}
